<template>
    <div class="page-container">
      <div class="card-wrapper">

        <div class="pageTitle">
          <h3>Guaranteed Rent</h3>
        </div>
        
        <!-- Top Illustration Header Section -->
        <div class="illustration-container">
          <div class="main-shield-bg">
            <div class="main-shield-icon">
              <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 28C19.4 24.9333 21.5833 22.775 22.55 21.525C23.5167 20.275 24 19.0333 24 17.8C24 16.6 23.5667 15.5667 22.7 14.7C21.8333 13.8333 20.8 13.4 19.6 13.4C18.9 13.4 18.225 13.5417 17.575 13.825C16.925 14.1083 16.4 14.5 16 15C15.6 14.5 15.0833 14.1083 14.45 13.825C13.8167 13.5417 13.1333 13.4 12.4 13.4C11.2 13.4 10.1667 13.8333 9.3 14.7C8.43333 15.5667 8 16.6 8 17.8C8 18.4333 8.08333 19.0167 8.25 19.55C8.41667 20.0833 8.78333 20.7083 9.35 21.425C9.91667 22.1417 10.725 23.0167 11.775 24.05C12.825 25.0833 14.2333 26.4 16 28ZM16 40C11.3667 38.8333 7.54167 36.175 4.525 32.025C1.50833 27.875 0 23.2667 0 18.2V6L16 0L32 6V18.2C32 23.2667 30.4917 27.875 27.475 32.025C24.4583 36.175 20.6333 38.8333 16 40Z" fill="white"/>
              </svg>

            </div>
            <div class="mini-badge">
              <svg width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.33333 21C1.69167 21 1.14236 20.7715 0.685417 20.3146C0.228472 19.8576 0 19.3083 0 18.6667V2.33333C0 1.69167 0.228472 1.14236 0.685417 0.685417C1.14236 0.228472 1.69167 0 2.33333 0H18.6667C19.3083 0 19.8576 0.228472 20.3146 0.685417C20.7715 1.14236 21 1.69167 21 2.33333H11.6667C10.2861 2.33333 9.16319 2.76597 8.29792 3.63125C7.43264 4.49653 7 5.61944 7 7V14C7 15.3806 7.43264 16.5035 8.29792 17.3687C9.16319 18.234 10.2861 18.6667 11.6667 18.6667H21C21 19.3083 20.7715 19.8576 20.3146 20.3146C19.8576 20.7715 19.3083 21 18.6667 21H2.33333ZM11.6667 16.3333C11.025 16.3333 10.4757 16.1049 10.0188 15.6479C9.56181 15.191 9.33333 14.6417 9.33333 14V7C9.33333 6.35833 9.56181 5.80903 10.0188 5.35208C10.4757 4.89514 11.025 4.66667 11.6667 4.66667H19.8333C20.475 4.66667 21.0243 4.89514 21.4813 5.35208C21.9382 5.80903 22.1667 6.35833 22.1667 7V14C22.1667 14.6417 21.9382 15.191 21.4813 15.6479C21.0243 16.1049 20.475 16.3333 19.8333 16.3333H11.6667ZM15.1667 12.25C15.6722 12.25 16.0903 12.0847 16.4208 11.7542C16.7514 11.4236 16.9167 11.0056 16.9167 10.5C16.9167 9.99444 16.7514 9.57639 16.4208 9.24583C16.0903 8.91528 15.6722 8.75 15.1667 8.75C14.6611 8.75 14.2431 8.91528 13.9125 9.24583C13.5819 9.57639 13.4167 9.99444 13.4167 10.5C13.4167 11.0056 13.5819 11.4236 13.9125 11.7542C14.2431 12.0847 14.6611 12.25 15.1667 12.25Z" fill="#0033EA"/>
              </svg>
            </div>
          </div>
        </div>
  
        <!-- Main Typography Content -->
        <div class="text-header">
          <h1 class="main-title">Income Peace of Mind</h1>
          <p class="subtitle">Financial security for your rental portfolio with zero payment delays.</p>
        </div>
  
        <!-- Features List Cards -->
        <div class="features-list">
          <div v-for="(feature, index) in features" :key="index" class="feature-item">
            <div class="icon-wrapper" :class="feature.iconBgClass">
              <svg viewBox="0 0 24 24" class="svg-icon" :class="feature.iconColorClass">
                <path :d="feature.svgPath" />
                <circle v-if="feature.svgCircle" :cx="feature.svgCircle.cx" :cy="feature.svgCircle.cy" :r="feature.svgCircle.r" />
                <rect v-if="feature.svgRect" :x="feature.svgRect.x" :y="feature.svgRect.y" :width="feature.svgRect.w" :height="feature.svgRect.h" :rx="feature.svgRect.rx" />
              </svg>
            </div>
            <div class="feature-text">
              <h2 class="feature-title">{{ feature.title }}</h2>
              <p class="feature-desc">{{ feature.description }}</p>
            </div>
          </div>
        </div>
        

        <section class="workflow-section">
          <h2 class="section-title">How It Works</h2>
          
          <div class="timeline">
            <div v-for="(step, index) in steps" :key="index" class="timeline-item">
              <div class="timeline-badge">
                <span class="step-number">{{ step.number }}</span>
              </div>
              <div class="timeline-content">
                <p class="step-text">{{ step.text }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="checklist-card">
          <h3 class="checklist-header">REQUIREMENTS CHECKLIST</h3>
          
          <ul class="checklist-items">
            <li v-for="(item, index) in checklist" :key="index" class="checklist-item">
              <div class="check-icon-wrapper">
                <!-- Verified Circle Check Blue SVG Icon -->
                <svg viewBox="0 0 24 24" class="svg-check-icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <span class="item-label">{{ item }}</span>
            </li>
          </ul>
        </section>

        <div class="notice-box">
          <div class="notice-icon-wrapper">
            <!-- Information SVG Icon -->
            <svg viewBox="0 0 24 24" class="svg-info-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
          </div>
          <div class="notice-content">
            <p class="notice-text">
              <strong>Important Note:</strong> Funds are first received as Uncleared Balance before becoming available for withdrawal.
            </p>
          </div>
        </div>
      
        <div class="button-holder">
          <button class="btn" @click="routeTo()">Continue</button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'HomeGRequest',
    data() {
      return {
        features: [
          {
            title: 'Get Paid On Time',
            description: 'Receive rent on the due date',
            iconBgClass: 'bg-light-blue',
            iconColorClass: 'blue-fill',
            svgPath: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 10H7v-2h4V6h2v6z'
          },
          {
            title: 'Income Protection',
            description: 'Secured and predictable income',
            iconBgClass: 'bg-light-blue-shield',
            iconColorClass: 'blue-stroke-only',
            svgPath: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'
          },
          {
            title: 'SAFE',
            description: 'Funds paid directly to your SAFE',
            iconBgClass: 'bg-dark-blue',
            iconColorClass: 'white-icon',
            svgRect: { x: 4, y: 6, w: 16, h: 12, rx: 2 },
            svgPath: 'M4 10h16'
          }
        ],
          steps: [
          { number: 1, text: 'Select your property' },
          { number: 2, text: 'Activate Guaranteed Rent' },
          { number: 3, text: 'Receive Rent on Due Date' }
        ],
        checklist: [
          'SAFE Activation',
          'Profile Verification',
          'Ownership Verification',
          'Tenancy Agreements',
          'No outstanding issues'
        ]
      };
    },
    methods: {
      routeTo() {
        this.$router.push("guarentee-rent-agreement");
      }
    }
  };
  </script>
  
  <style lang="scss" scoped>
  // Variables Configuration
  $color-bg-main: #f8faff;
  $color-bg-card: #ffffff;
  $color-text-primary: #111625;
  $color-text-secondary: #60687b;
  $color-brand-blue: #0046ff;
  $color-brand-light-blue: #eef2ff;
  $color-brand-dark-blue: #0033EA;

  $color-bg-checklist: #e8ecfc;
  $color-bg-notice: #f0f3fa;
  $color-text-dark: #090e1a;
  $color-text-muted: #4e5668;
  
  // Typography Base Mixin
  @mixin text-style($size, $weight, $color, $margin: 0) {
    font-size: $size;
    font-weight: $weight;
    color: $color;
    margin: $margin;
  }

  .pageTitle {
    width: 100%;
    margin-top: 40px;
    margin-bottom: 30px;
      h3 {
        font-family: Lato;
        font-weight: 600;
        font-size: 24px;
        line-height: 28px;
        letter-spacing: 0%;
        text-align: center;
        // color: 
      }
  }
  
  .page-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: $color-bg-main;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    padding: 20px;
    box-sizing: border-box;
  
    .card-wrapper {
      width: 100%;
      max-width: 480px;
      display: flex;
      flex-direction: column;
      align-items: center;
      // background: $color-bg-card;
      background: #f8faff;
      box-shadow: 
        0 0 0 1px rgba(0, 0, 0, 0.05), 
        0 4px 12px rgba(0, 0, 0, 0.05), 
        0 12px 24px rgba(0, 0, 0, 0.05);
        padding: 15px;
        border-radius: 6px;

    }
  
    // Header Illustration Wrapper
    .illustration-container {
      margin-bottom: 28px;
  
      .main-shield-bg {
        position: relative;
        width: 160px;
        height: 160px;
        background-color: #e3ecff;
        border-radius: 48px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
  
      .main-shield-icon {
        width: 110px;
        height: 110px;
        background-color: $color-brand-blue;
        border-radius: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: 0 10px 25px rgba($color-brand-blue, 0.15);
      }
  
      .mini-badge {
        position: absolute;
        bottom: 4px;
        right: 4px;
        width: 42px;
        height: 42px;
        background-color: $color-bg-card;
        border-radius: 14px;
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }
    }
  
    // Header Text Context
    .text-header {
      text-align: center;
      margin-bottom: 36px;
      padding: 0 16px;
  
      .main-title {
        @include text-style(26px, 700, $color-text-primary, 0 0 12px 0);
        letter-spacing: -0.5px;
        font-family: Lato;
      }
  
      .subtitle {
        @include text-style(15px, 400, $color-text-secondary);
        line-height: 1.5;
        font-family: Lato;
      }
    }
  
    // Target Component Layout Array
    .features-list {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 16px;
  
      .feature-item {
        background-color: $color-bg-card;
        border-radius: 16px;
        padding: 18px 20px;
        display: flex;
        align-items: center;
        gap: 16px;
        box-shadow: 0 2px 8px rgba(0, 64, 128, 0.02), 0 12px 24px rgba(0, 64, 128, 0.03);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
  
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 64, 128, 0.04), 0 16px 32px rgba(0, 64, 128, 0.05);
        }
      }
    }
  
    // Nested Icon & Graphic Configurations
    .icon-wrapper {
      width: 44px;
      height: 44px;
      min-width: 44px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
  
      &.bg-light-blue {
        background-color: $color-brand-light-blue;
      }
  
      &.bg-light-blue-shield {
        background-color: $color-brand-light-blue;
        border-radius: 12px;
      }
  
      &.bg-dark-blue {
        background-color: $color-brand-dark-blue;
        border-radius: 10px;
  
        .svg-icon {
          fill: none;
          stroke: $color-bg-card;
          stroke-width: 2;
        }
      }
  
      .svg-icon {
        width: 24px;
        height: 24px;
      }
    }
  
    // Scalable Vector Graphics Modifiers
    .svg-icon {
      width: 22px;
      height: 22px;
  
      &.white-icon {
        fill: $color-bg-card;
      }
  
      &.blue-fill {
        fill: $color-brand-blue;
      }
  
      &.blue-stroke-only {
        fill: none;
        stroke: $color-brand-blue;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    }
  
    // Item Content Info Strings
    .feature-text {
      display: flex;
      flex-direction: column;
      gap: 4px;
  
      .feature-title {
        @include text-style(16px, 600, $color-text-primary);
        font-family: Lato;
        text-align: left;
      }
  
      .feature-desc {
        @include text-style(13.5px, 400, $color-text-secondary);
        font-family: Lato;
        text-align: left;
      }
    }
  }
  
  // Media Breakpoint Adaptations
  @media (max-width: 400px) {
    .page-container {
      .text-header {
        .main-title {
          font-size: 22px;
        }
        .subtitle {
          font-size: 14px;
        }
      }
      .features-list .feature-item {
        padding: 14px 16px;
      }
    }
  }

  // Workflow / Timeline Section Styling
  .workflow-section {
    width: 100%;
    margin-top: 46px;
    .section-title {
      font-size: 24px;
      font-weight: 700;
      color: $color-text-dark;
      margin: 0 0 28px 0;
      letter-spacing: -0.3px;
      text-align: left;
      font-family: Lato;
    }

    .timeline {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 32px;
      padding-left: 6px;

      // Vertical connecting line running down steps
      &::before {
        content: '';
        position: absolute;
        top: 14px;
        left: 20px;
        bottom: 14px;
        width: 2px;
        background-color: #e2e8f5;
        z-index: 1;
      }

      .timeline-item {
        position: relative;
        display: flex;
        align-items: center;
        gap: 20px;
        z-index: 2;

        .timeline-badge {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: $color-brand-blue;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0 4px 10px rgba($color-brand-blue, 0.2);

          .step-number {
            font-size: 14px;
            font-weight: 700;
            color: $color-bg-card;
          }
        }

        .timeline-content {
          .step-text {
            font-size: 17px;
            font-weight: 500;
            color: $color-text-dark;
            margin: 0;
            text-align: left;
            font-family: Lato;
          }
        }
      }
    }
  }

  // Requirements Checklist Box Styling
  .checklist-card {
    background-color: $color-bg-checklist;
    border-radius: 16px;
    padding: 24px 22px;
    box-shadow: 0 2px 12px rgba(0, 50, 150, 0.02);
    text-align: left;
    width: 100%;
    margin-top: 46px;
    margin-bottom: 30px;

    .checklist-header {
      font-size: 14px;
      font-weight: 700;
      color: $color-brand-blue;
      letter-spacing: 0.8px;
      margin: 0 0 24px 0;
      font-family: Lato;
    }

    .checklist-items {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 18px;

      .checklist-item {
        display: flex;
        align-items: center;
        gap: 14px;


        .check-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;

          .svg-check-icon {
            width: 21px;
            height: 21px;
            fill: $color-brand-blue;
          }
        }

        .item-label {
          font-size: 16px;
          font-weight: 500;
          color: #3b4254;
          font-family: Lato;
        }
      }
    }
  }

  // 3. Bottom Notice Alert Block Styling
  .notice-box {
    background-color: $color-bg-notice;
    border-radius: 12px;
    padding: 16px 18px;
    display: flex;
    gap: 12px;
    align-items: flex-start;

    .notice-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: 2px;

      .svg-info-icon {
        width: 20px;
        height: 20px;
        fill: $color-brand-blue;
      }
    }

    .notice-content {
      flex: 1;

      .notice-text {
        font-size: 14px;
        line-height: 1.5;
        color: $color-text-muted;
        margin: 0;
        text-align: left;
        font-family: Lato;

        strong {
          color: $color-text-dark;
          font-weight: 600;
        }
      }
    }
  }

  // Media viewports optimization for minor adjustments
  @media (max-width: 400px) {
    .page-container {
      .workflow-section {
        .section-title {
          font-size: 22px;
        }
        .timeline .timeline-item .step-text {
          font-size: 15.5px;
        }
      }
      
      .checklist-card {
        padding: 20px 16px;
        
        .checklist-items .checklist-item .item-label {
          font-size: 15px;
        }
      }
    }
  }
  
  .button-holder {
    margin-top: 30px;
    margin-bottom: 20px;
    width: 100%;
    button {
      background-color: $color-brand-dark-blue;
      color: $color-bg-card;
      width: 100%;
      height: 50px;
      font-family: Lato;
      box-shadow: none;
      outline: 0;
    }
  }
</style>
  